/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1790121600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Sep 23 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "dynata",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Dynata",
          "depth": 0,
          "id": "connect-to-dynata"
        }, {
          "value": "Syncing custom audiences",
          "depth": 1,
          "id": "syncing-custom-audiences"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Refresh rate",
          "depth": 1,
          "id": "refresh-rate"
        }, {
          "value": "Matched users count",
          "depth": 1,
          "id": "matched-users-count"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger-1"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      ul: "ul",
      li: "li",
      h3: "h3",
      h4: "h4",
      code: "code"
    }, _provideComponents(), props.components), {PylonLink, MatchBoosterDestination, CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!MatchBoosterDestination) _missingMdxReference("MatchBoosterDestination", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Sync your first-party audiences with Dynata data and insights solutions to\nbetter measure, evaluate, and optimize advertising effectiveness in critical\nareas of your media to see how a campaign influences consumers at all levels of\nthe purchase funnel."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audiences"
              })
            }), _jsx(_components.td, {
              children: "Create and update custom audiences"
            }), _jsx(_components.td, {
              children: "All"
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-dynata",
        children: "Connect to Dynata"
      }), "\n", _jsx(_components.p, {
        children: "This integration requires prior approval from the Dynata team and the Hightouch\nteam before proceeding."
      }), "\n", _jsxs(_components.p, {
        children: ["To gain approval, please ", _jsx(PylonLink, {
          pylonText: "Hi, I need approval to connect Dynata.",
          linkText: "contact the Hightouch support team"
        }), " and connect us directly\nwith your Dynata rep who will verify approval. Once we have confirmed, we will\nunlock the integration for you to connect."]
      }), "\n", _jsx(_components.p, {
        children: "To connect, input the following provided by your Dynata rep:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Access key"
        }), "\n", _jsx(_components.li, {
          children: "Secret key"
        }), "\n", _jsx(_components.li, {
          children: "Bucket name"
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-custom-audiences",
        children: "Syncing custom audiences"
      }), "\n", _jsx(_components.p, {
        children: "You can input the name of the audience you plan on delivering to Dynata."
      }), "\n", _jsx(_components.h4, {
        id: "user-identifiers",
        children: "User identifiers"
      }), "\n", _jsx(_components.p, {
        children: "To identify which users to add or update in an audience, select model columns\nand the corresponding Dynata fields. You can match on any of the following user\nfields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Unique ID (required for de-duping)"
        }), "\n", _jsx(_components.li, {
          children: "Email (SHA256)"
        }), "\n", _jsx(_components.li, {
          children: "Phone (SHA256)"
        }), "\n", _jsx(_components.li, {
          children: "MAID"
        }), "\n", _jsx(_components.li, {
          children: "IP address"
        }), "\n", _jsx(_components.li, {
          children: "First name"
        }), "\n", _jsx(_components.li, {
          children: "Last name"
        }), "\n", _jsx(_components.li, {
          children: "Address1"
        }), "\n", _jsx(_components.li, {
          children: "Address2"
        }), "\n", _jsx(_components.li, {
          children: "City"
        }), "\n", _jsx(_components.li, {
          children: "State"
        }), "\n", _jsx(_components.li, {
          children: "Postal code"
        }), "\n", _jsx(_components.li, {
          children: "Zip4"
        }), "\n", _jsx(_components.li, {
          children: "Country"
        }), "\n"]
      }), "\n", _jsx(MatchBoosterDestination, {
        destinationName: "Dynata"
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["The only supported sync mode is ", _jsx(_components.code, {
          children: "all"
        }), " mode, meaning that each sync run will refresh\nthe entire audience. Users removed from your audience will automatically be dropped\nwith each refresh - only present users will be in Dynata's audience."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "refresh-rate",
        children: "Refresh rate"
      }), "\n", _jsx(_components.p, {
        children: "Dynata has a max refresh rate of monthly. You should not have your Dynata syncs\nrunning more than every 4 weeks."
      }), "\n", _jsx(_components.h3, {
        id: "matched-users-count",
        children: "Matched users count"
      }), "\n", _jsx(_components.p, {
        children: "Match rates are not available to preview in the Hightouch platform. Contact your\nDynata rep for insights into match rates and audience reach."
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsxs(_components.p, {
        children: ["Live debugger view is not support for this integration. Please ", _jsx(PylonLink, {
          pylonText: "Hi, I'm seeing an issue with the Dynata destination.",
          linkText: "reach out to us"
        }), "\nif you see any issues."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger-1",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
